import React, { useEffect, useState, useMemo } from 'react';
import { HDate } from '@hebcal/core';
import { useHalachicData } from '../../hooks/useHalachicData';
import { getCalendarData, computeShabbatTitle } from '../../engine/providers/calendarProvider';
import { getLeyningData } from '../../engine/providers/leyningProvider';
import { computeShabbatEntryTime, computeShabbatExitTime } from './shabbatTimeUtils';
import apiService from '../../sdk/apiService';
import templates from './templates';
import './ShabbatWidget.css';

export const ShabbatWidget = ({
  widgetId,
  // Legacy props (no longer used)
  shabbat: legacyShabbat,
  loading: legacyLoading,
  error: legacyError,
  dispatch: legacyDispatch,
  location,
  customTimes = [],
  drashot = [],
  template = 'Times',
  title = 'זמני שבת',
  showTitle = true,
  entryIcon = 'Flame',
  exitIcon = 'Moon',
  entryZman = null,
  exitZman = null,
  bgColor,
  showTimeRule = false,
  showEntryExit = true,
  showLeyning = false,
  showHaftarah = false,
  leyningDisplayMode = 'summary',
}) => {
  // ── שליפה מהמנוע ההלכתי (רענון אוטומטי בשקיעה/חצות) ──────────────
  const engineConfig = {
    location: {
      lat: location?.lat,
      lng: location?.lng,
      timezone: location?.timezone || location?.tzid || 'Asia/Jerusalem',
    },
    isIsrael: true,
    edah: 'ashkenaz',
  };

  const { today } = useHalachicData(engineConfig);

  // ── state מקומי לנתוני שבת (זמני ערב שבת/שבת, הדלקת נרות, הבדלה) ──
  const [shabbat, setShabbat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!location?.lat || !location?.lng) return;

    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.fetchShabbatTimes(location);
        if (!cancelled) {
          setShabbat(data);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [location?.lat, location?.lng, today?.date]); // today?.date = re-fetch when engine refreshes after sunset/midnight

  // ── חישוב shabbatTitle מהמנוע (תיקון באג ״פרשת פרשה״) ─────────────
  const { shabbatTitle, shabbatTitleType, shabbatSubtitle } = useMemo(() => {
    try {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysToSat = dayOfWeek === 6 ? 0 : ((6 - dayOfWeek + 7) % 7 || 7);
      const satDate = new Date(now);
      satDate.setDate(satDate.getDate() + daysToSat);
      const satHDate = new HDate(satDate);
      const calData = getCalendarData(satHDate, true);
      return computeShabbatTitle(calData);
    } catch {
      return { shabbatTitle: null, shabbatTitleType: null, shabbatSubtitle: null };
    }
  }, [today?.date]);

  // ── חישוב נתוני קריאת התורה לשבת הקרובה ──────────────────────────
  const shabbatLeyning = useMemo(() => {
    if (!showLeyning && !showHaftarah) return null;
    try {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysToSat = dayOfWeek === 6 ? 0 : ((6 - dayOfWeek + 7) % 7 || 7);
      const satDate = new Date(now);
      satDate.setDate(satDate.getDate() + daysToSat);
      const satHDate = new HDate(satDate);
      return getLeyningData(satHDate, true);
    } catch {
      return null;
    }
  }, [today?.date, showLeyning, showHaftarah]);

  // שדרוג אובייקט shabbat עם כותרת מתוקנת מהמנוע
  const enhancedShabbat = useMemo(() => {
    if (!shabbat) return null;
    return {
      ...shabbat,
      meta: {
        ...shabbat.meta,
        parsha: shabbatTitle || shabbat.meta?.parsha || 'שבת',
        parshaShowPrefix: shabbatTitleType === 'parsha' || shabbatTitleType === 'combo',
        parshaSubtitle: shabbatSubtitle || null,
      },
    };
  }, [shabbat, shabbatTitle, shabbatTitleType, shabbatSubtitle]);

  // ── מצא דרשה מתאימה ────────────────────────────────────────────────
  // התאמה ראשית לפי תאריך (אמין יותר) — fallback לפי שם פרשה
  const currentParsha = enhancedShabbat?.meta?.parsha;
  const currentYear = new Date().getFullYear();
  const currentShabbatDate = (() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysToSat = dayOfWeek === 6 ? 0 : ((6 - dayOfWeek + 7) % 7 || 7);
    const sat = new Date(now);
    sat.setDate(sat.getDate() + daysToSat);
    return sat.toISOString().slice(0, 10);
  })();
  const matchedDrasha = (drashot || []).find(
    (d) => d.parshaDate === currentShabbatDate ||
           (d.parshaName === currentParsha && new Date(d.parshaDate).getFullYear() === currentYear)
  );
  const drashaText = matchedDrasha?.text || '';

  // ── חישוב זמנים יחסיים ──────────────────────────────────────────────
  const calculateRelativeTime = (baseTime, offset, unit, roundTo) => {
    if (!baseTime) return '--:--';
    try {
      let timeStr = baseTime;
      if (baseTime.includes('T')) {
        const date = new Date(baseTime);
        timeStr = date.toTimeString().slice(0, 5);
      } else if (baseTime.match(/^\d{2}:\d{2}:\d{2}$/)) {
        // HH:MM:SS (netzHachama from formatTimeWithSeconds) — strip seconds
        timeStr = baseTime.slice(0, 5);
      } else if (baseTime.match(/^\d{2}:\d{2}$/)) {
        timeStr = baseTime;
      } else {
        const date = new Date(baseTime);
        if (!isNaN(date.getTime())) {
          timeStr = date.toTimeString().slice(0, 5);
        } else {
          return '--:--';
        }
      }
      const [hours, minutes] = timeStr.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes;
      const offsetMinutes = unit === 'hours' ? offset * 60 : offset;
      const newTotalMinutes = totalMinutes + offsetMinutes;
      let finalMinutes = ((newTotalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
      if (roundTo && roundTo > 0) {
        const mins = finalMinutes % 60;
        const roundedMins = Math.floor(mins / roundTo) * roundTo;
        finalMinutes = Math.floor(finalMinutes / 60) * 60 + roundedMins;
      }
      const finalHours = Math.floor(finalMinutes / 60);
      const finalMins = finalMinutes % 60;
      return `${finalHours.toString().padStart(2, '0')}:${finalMins.toString().padStart(2, '0')}`;
    } catch (err) {
      console.error('Error calculating relative time:', err);
      return '--:--';
    }
  };

  // חישוב dayEntryTime ו-dayExitTime
  const dayEntryTime = computeShabbatEntryTime(enhancedShabbat, entryZman);
  const dayExitTime  = computeShabbatExitTime(enhancedShabbat, exitZman);

  // עיבוד זמנים מותאמים אישית — מעבר ראשון: זמני API
  const buildBaseTimeMap = (timeWithDay) => {
    if (!enhancedShabbat) return {};
    const dayData = timeWithDay.day === 'shabbat' ? enhancedShabbat.shabbat : enhancedShabbat.friday;
    return {
      'dayEntryTime': dayEntryTime,
      'dayExitTime':  dayExitTime,
      'candleLighting': enhancedShabbat.meta?.candleLighting,
      'havdalah':       enhancedShabbat.shabbat?.havdalah || enhancedShabbat.meta?.havdalah,
      'fridaySunset': enhancedShabbat.friday?.sunset,
      'shabbatSunset': enhancedShabbat.shabbat?.sunset,
      'fridayTzeit7083': enhancedShabbat.friday?.tzeit7083deg,
      'fridayTzeit85': enhancedShabbat.friday?.tzeit85deg,
      'fridayTzeit42': enhancedShabbat.friday?.tzeit42min,
      'fridayTzeit50': enhancedShabbat.friday?.tzeit50min,
      'fridayTzeit72': enhancedShabbat.friday?.tzeit72min,
      'shabbatTzeit7083': enhancedShabbat.shabbat?.tzeit7083deg,
      'shabbatTzeit85': enhancedShabbat.shabbat?.tzeit85deg,
      'shabbatTzeit42': enhancedShabbat.shabbat?.tzeit42min,
      'shabbatTzeit50': enhancedShabbat.shabbat?.tzeit50min,
      'shabbatTzeit72': enhancedShabbat.shabbat?.tzeit72min,
      'sofZmanShma':    enhancedShabbat.shabbat?.sofZmanShmaGra,
      'sofZmanShmaMga': enhancedShabbat.shabbat?.sofZmanShmaMga,
      'shabbatHavdalah': enhancedShabbat.shabbat?.havdalah || enhancedShabbat.meta?.havdalah || enhancedShabbat.havdalah,
      'daySunset':         dayData?.sunset,
      'dayTzeit7083':      dayData?.tzeit7083deg,
      'dayTzeit85':        dayData?.tzeit85deg,
      'dayTzeit42':        dayData?.tzeit42min,
      'dayTzeit50':        dayData?.tzeit50min,
      'dayTzeit72':        dayData?.tzeit72min,
      'dayNetz':           dayData?.netzHachama,
      'dayAlot':           dayData?.alotHaShachar,
      'dayChatzot':        dayData?.chatzot,
      'dayPlagHamincha':   dayData?.plagHamincha,
      'dayMinchaGedola':   dayData?.minchaGedola,
      'dayMinchaKetana':   dayData?.minchaKetana,
      'dayChatzotNight':   enhancedShabbat.shabbat?.chatzotNight,
      'dayHavdalah':       enhancedShabbat.shabbat?.havdalah || enhancedShabbat.meta?.havdalah || enhancedShabbat.havdalah,
      // סוף זמן שמע — כל וריאנטי המפתחות (GRA + MGA 16.1° + MGA 19.8°)
      'daySofZmanShma':            enhancedShabbat.shabbat?.sofZmanShmaGra,
      'daySofZmanShmaMga':         enhancedShabbat.shabbat?.sofZmanShmaMga,
      'daySofZmanShmaMGA':         enhancedShabbat.shabbat?.sofZmanShmaMga,
      'daySofZmanShmaMGA16Point1': enhancedShabbat.shabbat?.sofZmanShmaMga,
      'daySofZmanShmaMGA19Point8': enhancedShabbat.shabbat?.sofZmanShmaMga19 || enhancedShabbat.shabbat?.sofZmanShmaMga,
      // סוף זמן תפילה — כל וריאנטי המפתחות
      'daySofZmanTfilla':            enhancedShabbat.shabbat?.sofZmanTfilla,
      'daySofZmanTfillaMGA':         enhancedShabbat.shabbat?.sofZmanTfillaMGA,
      'daySofZmanTfillaMGA16Point1': enhancedShabbat.shabbat?.sofZmanTfillaMGA,
      'daySofZmanTfillaMGA19Point8': enhancedShabbat.shabbat?.sofZmanTfillaMGA,
      // זמני חמץ — רלוונטי רק כשערב פסח חל בשבת (נדיר; לא מחושב ב-API שבת)
      'daySofZmanAchilatChametz':    null,
      'daySofZmanBiurChametz':       null,
      'daySofZmanAchilatChametzMGA': null,
      'daySofZmanBiurChametzMGA':    null,
    };
  };

  const pass1 = customTimes.map(ct => {
    const timeWithDay = { ...ct, day: ct.day || 'friday' };
    if (timeWithDay.type === 'relative' && enhancedShabbat && !String(timeWithDay.baseTime || '').startsWith('ct:')) {
      const calculatedTime = calculateRelativeTime(buildBaseTimeMap(timeWithDay)[timeWithDay.baseTime], timeWithDay.offset, timeWithDay.unit, timeWithDay.roundTo);
      return { ...timeWithDay, time: calculatedTime };
    }
    return timeWithDay;
  });

  // מעבר שני: זמנים יחסיים לזמן מותאם אחר (ct: prefix)
  const ctTimeMap = {};
  const ctLabelMap = {};
  pass1.forEach(ct => {
    if (ct.id) {
      if (ct.time) ctTimeMap[ct.id] = ct.time;
      if (ct.label) ctLabelMap[ct.id] = ct.label;
    }
  });

  const processedCustomTimes = pass1.map(ct => {
    if (ct.type === 'relative' && String(ct.baseTime || '').startsWith('ct:')) {
      const refId = ct.baseTime.slice(3);
      const refTime = ctTimeMap[refId];
      if (!refTime || refTime === '--:--') return { ...ct, time: '--:--' };
      return { ...ct, time: calculateRelativeTime(refTime, ct.offset, ct.unit, ct.roundTo), _baseLabel: ctLabelMap[refId] || '' };
    }
    return ct;
  });

  const Template = templates[template];

  if (!Template) {
    return <div>Template not found: {template}</div>;
  }

  return (
    <Template
      shabbat={enhancedShabbat}
      processedCustomTimes={processedCustomTimes}
      drashaText={drashaText}
      loading={loading}
      error={error}
      title={title}
      showTitle={showTitle}
      entryIcon={entryZman?.icon || entryIcon}
      exitIcon={exitZman?.icon || exitIcon}
      dayEntryTime={dayEntryTime}
      dayExitTime={dayExitTime}
      bgColor={bgColor}
      showTimeRule={showTimeRule}
      showEntryExit={showEntryExit}
      shabbatLeyning={shabbatLeyning}
      showLeyning={showLeyning}
      showHaftarah={showHaftarah}
      leyningDisplayMode={leyningDisplayMode}
    />
  );
};
